import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TeamMapDialog from "@/components/TeamMapDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MagnifyingGlass, HardHat, MapPin, MapTrifold, Trash, CheckSquare, Square, X as XIcon } from "@phosphor-icons/react";
import { CHANTIER_TYPE_META } from "@/pages/chantier/constants";

const STATUS_META = {
  waiting: { label: "En attente", color: "#9CA3AF" },
  in_progress: { label: "En cours", color: "#005CFF" },
  urgent: { label: "Urgent", color: "#EF4444" },
  done: { label: "Terminé", color: "#10B981" },
};

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "in_progress", label: "En cours" },
  { key: "urgent", label: "Urgent" },
  { key: "waiting", label: "En attente" },
  { key: "done", label: "Terminé" },
];

export default function Chantiers() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [chantiers, setChantiers] = useState([]);
  const [clients, setClients] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [mapOpen, setMapOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null); // chantier object or null
  const [deleting, setDeleting] = useState(false);

  // Bulk selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      const [ch, cl] = await Promise.all([api.get("/chantiers"), api.get("/clients")]);
      setChantiers(ch.data);
      setClients(Object.fromEntries(cl.data.map((c) => [c.id, c])));
      setLoading(false);
    })();
  }, []);

  const filtered = chantiers
    .filter((c) => (filter === "all" ? true : c.status === filter))
    .filter((c) => (q ? (c.name + " " + (c.address || "") + " " + (clients[c.client_id]?.name || "")).toLowerCase().includes(q.toLowerCase()) : true));

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/chantiers/${toDelete.id}`);
      setChantiers((cs) => cs.filter((x) => x.id !== toDelete.id));
      toast.success("Chantier supprimé");
      setToDelete(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const selectAllVisible = () => {
    const allIds = filtered.map((c) => c.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post("/chantiers/bulk-delete", { ids });
      setChantiers((cs) => cs.filter((x) => !selected.has(x.id)));
      toast.success(`${r.data.deleted} chantier(s) supprimé(s)`);
      setConfirmBulk(false);
      exitSelectMode();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkDeleting(false); }
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  return (
    <div className="p-4 space-y-4">
      {/* Search + team map trigger */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher chantier, client, adresse…"
            className="h-12 pl-11 rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/30"
            data-testid="chantiers-search"
          />
        </div>
        {isAdmin && (
          <Button
            onClick={() => setMapOpen(true)}
            className="h-12 px-4 rounded-2xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400"
            data-testid="team-map-btn"
            title="Carte équipe temps réel"
          >
            <MapTrifold size={18} weight="duotone" />
            <span className="hidden sm:inline text-xs uppercase tracking-widest ml-1">Équipe</span>
          </Button>
        )}
        {isAdmin && !selectMode && (
          <Button
            onClick={() => setSelectMode(true)}
            className="h-12 px-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
            data-testid="bulk-mode-toggle"
            title="Sélection multiple"
          >
            <CheckSquare size={18} weight="duotone" />
            <span className="hidden sm:inline text-xs uppercase tracking-widest ml-1">Sélection</span>
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectMode && (
        <div className="rounded-2xl bg-[#005CFF]/10 border border-[#005CFF]/30 p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <button
            onClick={selectAllVisible}
            className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white"
            data-testid="bulk-select-all"
          >
            {allVisibleSelected ? <CheckSquare size={16} weight="fill" className="text-[#005CFF]" /> : <Square size={16} />}
            <span>{allVisibleSelected ? "Tout désélectionner" : "Tout sélectionner"}</span>
          </button>
          <span className="text-xs text-white/50" data-testid="bulk-selected-count">
            {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
          </span>
          <div className="flex-1" />
          <Button
            onClick={() => setConfirmBulk(true)}
            disabled={selected.size === 0}
            className="h-9 px-3 rounded-full bg-red-500 hover:bg-red-600 text-white disabled:opacity-40"
            data-testid="bulk-delete-btn"
          >
            <Trash size={14} weight="bold" />
            <span className="ml-1.5 text-xs uppercase tracking-widest">Supprimer</span>
          </Button>
          <button
            onClick={exitSelectMode}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 flex items-center justify-center"
            title="Quitter le mode sélection"
            data-testid="bulk-cancel-btn"
          >
            <XIcon size={14} weight="bold" />
          </button>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="chantiers-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 text-xs uppercase tracking-widest px-3 py-2 rounded-full border transition-colors ${
              filter === f.key
                ? "bg-[#005CFF] border-[#005CFF] text-white"
                : "bg-white/5 border-white/10 text-white/60"
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Cards */}
      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/15 p-8 text-center">
          <HardHat size={32} className="mx-auto text-white/40 mb-2" weight="duotone" />
          <div className="font-display font-semibold">Aucun chantier</div>
          <div className="text-xs text-white/50 mt-1">Utilisez le bouton + pour en créer un</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const cl = clients[c.client_id];
            const meta = STATUS_META[c.status];
            const chType = c.type ? CHANTIER_TYPE_META[c.type] : null;
            const isSel = selected.has(c.id);
            const cardClasses = `w-full text-left rounded-2xl border p-4 transition-colors relative group ${
              isSel ? "bg-[#005CFF]/10 border-[#005CFF]/50" : "bg-white/[0.03] border-white/10 hover:border-white/20"
            }`;
            const onCardClick = () => {
              if (selectMode) toggleSel(c.id);
              else nav(`/chantiers/${c.id}`);
            };
            return (
              <div key={c.id} className={cardClasses} data-testid={`chantier-card-${c.id}`}>
                <button
                  onClick={onCardClick}
                  className="w-full text-left active:scale-[0.99] transition-transform"
                  data-testid={`chantier-card-open-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {selectMode && (
                        <div className="pt-1 shrink-0" data-testid={`chantier-sel-${c.id}`}>
                          {isSel ? (
                            <CheckSquare size={22} weight="fill" className="text-[#005CFF]" />
                          ) : (
                            <Square size={22} className="text-white/40" />
                          )}
                        </div>
                      )}
                      <div className="w-1 h-12 rounded-full shrink-0" style={{ background: c.color || cl?.color || meta.color }} data-testid={`chantier-dot-${c.id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5 flex-wrap">
                          <span>{cl?.name || "—"}</span>
                          {chType && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold normal-case tracking-normal"
                              style={{ background: `${chType.color}22`, color: chType.color }}
                              data-testid={`chantier-type-badge-${c.id}`}
                            >
                              <span>{chType.icon}</span> {chType.label}
                            </span>
                          )}
                        </div>
                        <div className="font-display font-semibold truncate mt-0.5">{c.name}</div>
                        {c.address && (
                          <div className="text-xs text-white/50 flex items-center gap-1 mt-1"><MapPin size={12} /> <span className="truncate">{c.address}</span></div>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap"
                      style={{ background: `${meta.color}22`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-[width]" style={{ width: `${c.progress}%`, background: meta.color }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/50">
                    <span>{c.sous_chantiers?.length || 0} sous-chantiers</span>
                    <span>{c.progress}%</span>
                  </div>
                </button>
                {isAdmin && !selectMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setToDelete(c); }}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 md:opacity-0 focus:opacity-100 transition-opacity"
                    title="Supprimer"
                    data-testid={`chantier-delete-${c.id}`}
                  >
                    <Trash size={12} weight="duotone" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <TeamMapDialog open={mapOpen} onClose={() => setMapOpen(false)} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={doDelete}
        loading={deleting}
        title="Supprimer ce chantier ?"
        description={toDelete ? `« ${toDelete.name} » — toutes les interventions, photos et rapports liés seront également supprimés. Irréversible.` : ""}
        confirmLabel="Supprimer"
        testid="chantiers-delete-confirm"
      />
      <ConfirmDialog
        open={confirmBulk}
        onOpenChange={setConfirmBulk}
        onConfirm={doBulkDelete}
        loading={bulkDeleting}
        title={`Supprimer ${selected.size} chantier${selected.size > 1 ? "s" : ""} ?`}
        description={`Toutes les interventions, photos, tâches et rapports liés à ces chantiers seront également supprimés. Cette action est irréversible.`}
        confirmLabel={`Supprimer ${selected.size} chantier${selected.size > 1 ? "s" : ""}`}
        testid="bulk-delete-confirm"
      />
    </div>
  );
}
